import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next';
import SMCTextInput from '../../../components/Simple/SMCTextInput';
import {SMCSelect, SMCOption} from '../../../components/Simple/SMCSelect';
import {AlertSchema} from '../../../api/DBSchema/Alert';
import {SMCCheckbox} from '../../../components/Simple/SMCCheckbox';
import SMCNumberInput from '../../../components/Simple/SMCNumberInput';
import SMCLabel from '../../../components/Simple/SMCLabel';
import RowLayout from '../../../components/Layouts/RowLayout';
import {hasValue} from '../../../utils/utils';

function DetailsTab({
    alertObject,
    setAlertObjectValue,
    logIdList
}) {
    const { t } = useTranslation();

    const name         = alertObject[AlertSchema.name];
    const description  = alertObject[AlertSchema.description];
    const logId        = alertObject[AlertSchema.logId];
    const severity     = alertObject[AlertSchema.severity];
    const filter       = alertObject[AlertSchema.filter];
    const isAtLeast    = alertObject[AlertSchema.isAtLeast];
    const threshold    = alertObject[AlertSchema.threshold];
    const isSameSource = alertObject[AlertSchema.isSameSource];
    const timeInterval = alertObject[AlertSchema.timeInterval];
    const isSendMail   = alertObject[AlertSchema.isSendMail];

    const data         = alertObject[AlertSchema.data];
    const enabled      = data[AlertSchema.dataSchema.enabled];

    const getSeverityByLogId = useCallback(logId => {
        let severity = undefined;
        if (logId && logIdList) {
            const logIdObject = logIdList.find(logIdObj => logIdObj.code === logId);
            if (logIdObject) {
                severity = logIdObject.severity;
            }
        }
        return severity;
    }, [logIdList]);

    return (
        <div style={{ width: '100%' }}>
            <SMCTextInput
                value={name}
                onChange={e => setAlertObjectValue(AlertSchema.name, e.target.value)}
                label={t('SMC_NOTIFY_ALERT_ID')}
                labelWidth={80}
                width={600}
            />
            <SMCCheckbox
                checked={enabled}
                onChange={e => setAlertObjectValue([AlertSchema.data, AlertSchema.dataSchema.enabled], e.target.checked)}
                label={t('SMC_NOTIFY_ALERT_ENABLED')}
                labelPosition="start"
                labelWidth={80}
            />
            <SMCTextInput
                value={description}
                onChange={e => setAlertObjectValue(AlertSchema.description, e.target.value)}
                label={t('SMC_NOTIFY_ALERT_DESC')}
                labelWidth={80}
                width={600}
            />
            <SMCLabel bold>{t('SMC_NOTIFY_ALERT_FILTER')}</SMCLabel>
            <SMCSelect
                value={logId}
                onChange={e => {
                    if (hasValue(e.target.value)) {
                        setAlertObjectValue(AlertSchema.logId, parseInt(e.target.value));
                        setAlertObjectValue(AlertSchema.severity, getSeverityByLogId(parseInt(e.target.value)));
                    }
                    else {
                        setAlertObjectValue(AlertSchema.logId, null);
                    }
                }}
                label={t('SMC_NOTIFY_ALERT_FILTER_ID')}
                labelWidth={80}
                width={600}
            >
                <SMCOption value=""></SMCOption>
                {
                    logIdList && logIdList.map(logIdObject => (
                        <SMCOption value={logIdObject.code} key={logIdObject.code}>{logIdObject.code} - {logIdObject.message}</SMCOption>
                    ))
                }
            </SMCSelect>
            <SMCSelect
                value={severity}
                disabled={logId}
                onChange={e => setAlertObjectValue(AlertSchema.severity, e.target.value)}
                label={t('SMC_NOTIFY_ALERT_FILTER_SEV')}
                labelWidth={80}
            >
                <SMCOption value={AlertSchema.severityValues.emergency}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.EMERGENCY')}</SMCOption>
                <SMCOption value={AlertSchema.severityValues.alert}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.ALERT')}</SMCOption>
                <SMCOption value={AlertSchema.severityValues.critical}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.CRITICAL')}</SMCOption>
                <SMCOption value={AlertSchema.severityValues.error}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.ERROR')}</SMCOption>
                <SMCOption value={AlertSchema.severityValues.warning}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.WARNING')}</SMCOption>
                <SMCOption value={AlertSchema.severityValues.notice}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.NOTICE')}</SMCOption>
                <SMCOption value={AlertSchema.severityValues.info}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.INFO')}</SMCOption>
                <SMCOption value={AlertSchema.severityValues.debug}>{t('SMP.SETTINGS.LOGGING.LOG_LEVEL.DEBUG')}</SMCOption>
            </SMCSelect>
            <SMCTextInput
                value={filter}
                onChange={e => setAlertObjectValue(AlertSchema.filter, e.target.value)}
                label={t('SMC_NOTIFY_ALERT_FILTER_TXT')}
                labelWidth={80}
                width={600}
            />
            <SMCLabel bold>{t('SMC_NOTIFY_ALERT_REC')}</SMCLabel>
            <RowLayout spacing="4px">
                <SMCLabel>{t('SMC_NOTIFY_ALERT_REC_TRIG')}</SMCLabel>
                <SMCSelect
                    value={isAtLeast ? 'AT_LEAST' : 'LESS_THAN'}
                    onChange={e => setAlertObjectValue(AlertSchema.isAtLeast, e.target.value === 'AT_LEAST')}
                    width={120}
                >
                    <SMCOption value="AT_LEAST">{t('SMC_NOTIFY_ALERT_REC_ATLEAST')}</SMCOption>
                    <SMCOption value="LESS_THAN">{t('SMC_NOTIFY_ALERT_REC_LESS')}</SMCOption>
                </SMCSelect>
                <SMCNumberInput
                    value={threshold}
                    onChange={e => setAlertObjectValue(AlertSchema.threshold, parseInt(e.target.value))}
                    width={75}
                />
                <SMCLabel>{t('SMC_NOTIFY_ALERT_REC_SRC')}</SMCLabel>
                <SMCSelect
                    value={isSameSource ? 'SAME_SOURCE' : 'ANY_SOURCE'}
                    onChange={e => setAlertObjectValue(AlertSchema.isSameSource, e.target.value === 'SAME_SOURCE')}
                >
                    <SMCOption value="SAME_SOURCE">{t('SMC_NOTIFY_ALERT_REC_SRCSAME')}</SMCOption>
                    <SMCOption value="ANY_SOURCE">{t('SMC_NOTIFY_ALERT_REC_SRCANY')}</SMCOption>
                </SMCSelect>
                <SMCLabel>{t('SMC_NOTIFY_ALERT_REC_PERIOD')}</SMCLabel>
                <SMCNumberInput
                    value={timeInterval}
                    onChange={e => setAlertObjectValue(AlertSchema.timeInterval, parseInt(e.target.value))}
                    width={75}
                />
                <SMCLabel>{t('SMC_NOTIFY_ALERT_REC_PERIOD_MINS')}</SMCLabel>
            </RowLayout>
            <SMCLabel bold>{t('SMC_NOTIFY_ALERT_ACT')}</SMCLabel>
            <SMCSelect
                value={isSendMail ? 'SEND_MAIL' : 'LOG_ONLY'}
                onChange={e => setAlertObjectValue(AlertSchema.isSendMail, e.target.value === 'SEND_MAIL')}
                label={t('SMC_NOTIFY_ALERT_ACT_ACT')}
                labelWidth={80}
            >
                <SMCOption value="SEND_MAIL">{t('SMC_NOTIFY_ALERT_ACT_SEND')}</SMCOption>
                <SMCOption value="LOG_ONLY">{t('SMC_NOTIFY_ALERT_ACT_LOG')}</SMCOption>
            </SMCSelect>
        </div>
    );
}

export default DetailsTab;